/**
 * The manifest is the only cloud→device contract (CLAUDE.md invariant 1).
 *
 * These types are technical-design.md §11.2 verbatim, plus `settings.blocklist`
 * from design-addendum.md §2. architecture-v2.md §4.2 carries a different shape;
 * decision-log O110 is the open question of which is the contract, and the player
 * is written against §11.2 because it is the later and more specific document.
 * The deltas are listed in player/README.md so the choice is visible.
 *
 * Fields marked "forward-compatible" are not in §11.2 but are required by
 * optisigns-parity.md §1 (scaling modes, image duration, mute policy, portrait).
 * They are optional here and defaulted locally, so a §11.2 manifest that omits
 * them plays correctly and dwf-030 can add them without a player change.
 */

/**
 * Two classes, decision-log D215: `host_promo` and `cross_promo` are retired, so the
 * compiler cannot emit them and a loop is `paid` > `house` (dwf-061). This array is also
 * the runtime whitelist `parse.ts` validates against: an item carrying any other class —
 * a retired one, or one added after this bundle was built — is skipped and never played,
 * and the rest of the manifest is adopted (decision-log O198). Rejecting the whole manifest
 * instead would freeze every screen on this bundle on its last loop until it was replaced.
 */
export const PRIORITY_CLASSES = ['paid', 'house'] as const;
export type PriorityClass = (typeof PRIORITY_CLASSES)[number];

/** Never distort is the rule; `stretch` exists for parity but is never a default. */
export const SCALING_MODES = ['fit', 'fill', 'stretch'] as const;
export type ScalingMode = (typeof SCALING_MODES)[number];

export const ORIENTATIONS = ['landscape', 'portrait'] as const;
export type Orientation = (typeof ORIENTATIONS)[number];

export interface ManifestItem {
  readonly item_id: string;
  readonly campaign_id: string;
  readonly creative_id: string;
  readonly venue_id: string;
  readonly asset_url: string;
  readonly asset_sha256: string;
  readonly media_type: string;
  readonly duration_ms: number;
  readonly priority_class: PriorityClass;
  /** ISO-8601 UTC, or null for an item with no end date. */
  readonly ends_at: string | null;
  /** Forward-compatible: optisigns-parity §1 scaling modes. Defaults to `fit`. */
  readonly scaling_mode?: ScalingMode;
  /** Forward-compatible: background fill behind a letterboxed/pillarboxed item. */
  readonly background_color?: string;
  /** Forward-compatible: per-item audio. Absent means muted (parity §1). */
  readonly audio_enabled?: boolean;
}

export interface QuietHours {
  /** Screen-local wall clock, `HH:MM`. Compiled tz handling stays server-side. */
  readonly start: string;
  readonly end: string;
}

export interface ManifestSettings {
  readonly heartbeat_interval_seconds: number;
  readonly event_batch_size: number;
  readonly cache_max_bytes: number;
  readonly quiet_hours: QuietHours | null;
  /** design-addendum §2 tier 2 takedown. Capped at 50 entries by the publisher. */
  readonly blocklist?: readonly string[];
  /** Forward-compatible: `screens.orientation`. Absent means detect from viewport. */
  readonly screen_orientation?: Orientation;
}

export interface Manifest {
  readonly manifest_version: number;
  readonly screen_id: string;
  readonly issued_at: string;
  readonly poll_interval_seconds: number;
  readonly loop: readonly ManifestItem[];
  readonly settings: ManifestSettings;
}

/** A manifest plus the ETag it was served with, as cached on disk. */
export interface StoredManifest {
  readonly manifest: Manifest;
  readonly etag: string | null;
  readonly fetched_at_ms: number;
}
